import { IonButton, IonIcon, IonInput, IonSelect, IonSelectOption } from '@ionic/react';
import { checkmarkOutline } from 'ionicons/icons';
import { useState } from 'react';
import { rememberCustomUom, uomLabel, uomOptions } from '../lib/uom.js';

const CUSTOM_SENTINEL = '__custom_uom__';

interface UomSelectProps {
  value: string;
  onChange: (uom: string) => void;
  label?: string;
  labelPlacement?: 'stacked' | 'fixed' | 'floating' | 'start' | 'end';
  /** T-316: override esplicito della lista opzioni (es. solo UdM base kg/pz/l).
   *  Se assente usa `uomOptions(value)` (canoniche + personalizzate + corrente). */
  options?: readonly string[];
  /** T-316: mostra "+ Altro…" per digitare un'UdM libera. Default true; per il
   *  selettore "Unità base" va false (le base sono un insieme chiuso). */
  allowCustom?: boolean;
  disabled?: boolean;
}

/**
 * T-290 — Selettore UdM con scelte PREDEFINITE (canoniche + personalizzate
 * ricordate) e fallback a campo libero ("+ Altro…"). Un'UdM libera digitata
 * viene memorizzata e ricompare poi tra le scelte suggerite (richiesta utente:
 * "una riga libera che va a popolare le scelte fisse e suggerite"). Riusato in
 * carico merce (review DDT/fattura) e in anagrafica alimento, così il magazzino
 * non si riempie di unità incoerenti che poi rompono lo scarico ricette.
 */
export function UomSelect({
  value,
  onChange,
  label = 'UdM',
  labelPlacement = 'stacked',
  options,
  allowCustom = true,
  disabled = false,
}: UomSelectProps): JSX.Element {
  const [customMode, setCustomMode] = useState(false);
  const [draft, setDraft] = useState('');

  // Lista opzioni: quella esplicita (T-316) oppure il default canonico. Il valore
  // corrente, se fuori lista, va comunque incluso così non sparisce dalla select.
  const baseOpts = options ? [...options] : uomOptions(value);
  const opts = value && !baseOpts.some((u) => u.toLowerCase() === value.toLowerCase())
    ? [...baseOpts, value]
    : baseOpts;

  if (customMode) {
    const commit = (): void => {
      const u = draft.trim();
      if (u) {
        rememberCustomUom(u);
        onChange(u);
      }
      setCustomMode(false);
      setDraft('');
    };
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, width: '100%' }}>
        <IonInput
          label={`${label} (personalizzata)`}
          labelPlacement={labelPlacement}
          placeholder="es. busta, vaschetta, secchio"
          value={draft}
          disabled={disabled}
          onIonInput={(ev) => setDraft(String(ev.detail.value ?? ''))}
        />
        <IonButton disabled={disabled} size="small" fill="clear" aria-label="Conferma unità" onClick={commit}>
          <IonIcon icon={checkmarkOutline} slot="icon-only" />
        </IonButton>
      </div>
    );
  }

  return (
    <IonSelect
      label={label}
      labelPlacement={labelPlacement}
      interface="popover"
      value={value}
      disabled={disabled}
      onIonChange={(ev) => {
        const v = String(ev.detail.value ?? '');
        if (v === CUSTOM_SENTINEL) {
          setDraft('');
          setCustomMode(true);
          return;
        }
        onChange(v);
      }}
    >
      {opts.map((u) => (
        <IonSelectOption key={u} value={u}>
          {uomLabel(u)}
        </IonSelectOption>
      ))}
      {allowCustom && <IonSelectOption value={CUSTOM_SENTINEL}>+ Altro…</IonSelectOption>}
    </IonSelect>
  );
}
